/* IBM Confidential‌ - OCO Source Materials‌ - (C) COPYRIGHT IBM CORP. 2021-2024 - The source code for this program is not published or otherwise‌ divested of its trade secrets, irrespective of what has been‌ deposited with the U.S. Copyright Office. */

import { AppMsg } from "../utils";
import isEmpty from "lodash/isEmpty";

const CURRENT_PWD_VALIDATION_ONLY = true;
const NEW_PWD_VALIDIDATION = false;
const NEW_PWD_NOT_NEEDED = '';

export const AuthenticationService = {
    login,
    logout,
    changePassword,
    changePasswordCurrentPwdValidationOnly,
    initPasswordRules, 
    resetPassword,
    initCustomPasswordRules,
    parseCustomPasswordRule
};

function login(username, password, loginObj) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: username, password: password})
    };

    //Handle Authentication END-point URL
    let authenticationUrl = _handleAuthenticationUrl(loginObj);
   
    return fetch(authenticationUrl, requestOptions)
        .then(handleLoginResponse);
}

function _handleAuthenticationUrl(loginObj) {
    if (loginObj.isTririgaUX) {
        return '../../p/websignon/signon';
    } else if (loginObj.isAdmin) {
        return '../../p/websignon/tririga-admin-login' +
            '?screenWidth='+loginObj.screenWidth+
            '&screenHeight='+loginObj.screenHeight+
            '&contentType='+loginObj.contentType+
            '&languageId='+loginObj.languageId
    } else {
        let loginUrl = '../../p/websignon/tririga-login' +
            '?screenWidth='+loginObj.screenWidth+
            '&screenHeight='+loginObj.screenHeight+
            '&screenDPI='+loginObj.screenDPI+
            '&contentType='+loginObj.contentType+
            '&languageId='+loginObj.languageId+
            '&ieFlag='+loginObj.ieFlag+
            '&ExtReqURL='+loginObj.ExtReqURL;
        if (loginObj.state && loginObj.state != null && loginObj.state.length > 0) {
            loginUrl = loginUrl + '&state='+loginObj.state;
        }
        return loginUrl;
    }
}

function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('user');
}

function handleLoginResponse( response )
{
    if (!response.ok) {
        // auto logout if response failed
        logout();
        return response.text().then(text => {
            if (!text || text === "") {
                return Promise.reject(AppMsg.getMessage(AppMsg.APP_SYSTEM_ERROR));
            }
            if (response.status === 401) {
                if (text.includes('Password Change Needed')) {
                    //Push history so back button goes back to login page.
                    let historyObj = { title: window.title, url: window.location.href };
                    window.history.pushState(historyObj, historyObj.title, historyObj.url);

                    let passwordChangeComponents = text.split(",passwordRules");
                    let passwordRulesString = _decodePasswordRulesString(passwordChangeComponents[1]);
                    return Promise.reject("Password change is required." + ";" + passwordRulesString);
                }
                return Promise.reject("Invalid username or password");
            }
            if (response.status === 403) {
                // Mixed mode SSO login error
                if (text.startsWith("Error 403: ")) {
                    const msg = text.substring(11, text.length);
                    if (msg != "") return Promise.reject(msg);
                }
                return Promise.reject(AppMsg.getMessage(AppMsg.APP_ERROR_NOT_ALLOWED));
            }
            try {
                return Promise.reject(JSON.parse(text));
            }
            catch (e) {
                return Promise.reject(text);
            }
        }).catch(
            (message ) => {
                if (message && message != "") throw message;
                throw AppMsg.getMessage(AppMsg.APP_SYSTEM_ERROR);
            });
    }
    else
    {
        return response.text().then(
            ( body ) =>
            {
                try
                {
                    const json = JSON.parse( body );
                    return json.url;
                }
                catch( e )
                {
                    throw body;
                }
            }
        ).catch(
            err => { throw err; }
        );
    }
}

function initPasswordRules() {
    return fetch("../../p/websignon/tririga-pwd-rules") 
        .then(response => {
        return response.json().then(data => {
            return data.passwordRules;
        });
    });
}

function _decodePasswordRulesString(passwordRules) {
    let document = new DOMParser().parseFromString(passwordRules, "text/html");
    return document.documentElement.textContent;
}

function changePasswordCurrentPwdValidationOnly(username, oldPassword) {
    return _changePassword(username, oldPassword, NEW_PWD_NOT_NEEDED, NEW_PWD_NOT_NEEDED, CURRENT_PWD_VALIDATION_ONLY);
}

function changePassword(username, oldPassword, newPassword, confirmNewPassword) {
    return _changePassword(username, oldPassword, newPassword, confirmNewPassword, NEW_PWD_VALIDIDATION);
}

function _changePassword(username, oldPassword, newPassword, confirmNewPassword, isCurrentPasswordValidationOnly) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: username, 
                                oldPassword: oldPassword,
                                newPassword: newPassword,
                                confirmNewPassword: confirmNewPassword,
                                isCurrentPasswordValidationOnly: isCurrentPasswordValidationOnly
                            })
    };

    return fetch('../../p/websignon/tririga-change-pwd', requestOptions).then(handlePasswordChangeResponse);
}

function handlePasswordChangeResponse(response) {
    if (!response.ok) {
        if (response.status === 401) {
            return response.text().then(text => {
                //First parse out status code in error message.
                let messageIndex = text.indexOf(": ");
                let errorMessage = messageIndex > -1 ? text.slice(messageIndex+2): text;
                return Promise.reject(errorMessage);
            });
            
        } else {
            return Promise.reject(AppMsg.getMessage(AppMsg.APP_SYSTEM_ERROR_PWD));
        }
    } else {
        //Password change functionality successful
        return Promise.resolve();
    }
}

function resetPassword(email) {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({email: email})
    };
    return fetch('../../p/websignon/tririga-reset-pwd', requestOptions).then(
        response => {
            if (!response.ok) {
                //Error on server error.
                if (response.status === 401) {
                    return Promise.reject("Email exceeded limit");
                }
                else if (response.status === 404) {
//                    return Promise.reject(AppMsg.getMessage(AppMsg.APP_ERROR_INVALID_EMAIL));
                    return Promise.resolve();
                }
                return Promise.reject(AppMsg.getMessage(AppMsg.APP_SYSTEM_ERROR_EMAIL));
            }
            //Resolve in most situations.    
            return Promise.resolve();
        }
    ).catch(
        (message) => {
            if (message && message != "") throw message;
            throw AppMsg.getMessage(AppMsg.APP_SYSTEM_ERROR);
        });
}

async function initCustomPasswordRules(username) {
    
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: username })
    };

    try {
        const response = await fetch("../../p/websignon/tririga-custom-pwd-rules", requestOptions);
        if (!response.ok) {
            throw new Error(`Failed to fetch custom password rules: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        await AppMsg.initMessages(data.userLanguageId);
        return parseCustomPasswordRule(data.customPasswordRules);
    } catch (error) {
        console.error("Error in initCustomPasswordRules:", error);
        throw AppMsg.getMessage(AppMsg.APP_SYSTEM_ERROR); 
    }
}

function parseCustomPasswordRule(customRules) {
    if (isEmpty(customRules)) return [];
  
    const rulesArray = [];
    let currentRule = "";
  
    for (let i = 0; i < customRules.length; i++) {
      const currentChar = customRules[i];
  
      if (currentChar === "," && !isWithinQuotes(customRules, i)) {
        rulesArray.push(currentRule.trim());
        currentRule = "";
      } else {
        currentRule += currentChar;
      }
    }
  
    rulesArray.push(currentRule.trim());
  
    return rulesArray.filter((rule) => !isEmpty(rule));
}
  
function isWithinQuotes(customRules, index) {
    let singleQuotes = false;
    let doubleQuotes = false;
  
    if (
      previousNonSpaceChar(customRules, index) === "'" &&
      nextNonSpaceChar(customRules, index) === "'"
    ) {
      singleQuotes = !singleQuotes;
    } else if (
      previousNonSpaceChar(customRules, index) === '"' &&
      nextNonSpaceChar(customRules, index) === '"'
    ) {
      doubleQuotes = !doubleQuotes;
    }
  
    return singleQuotes || doubleQuotes;
}
  
function nextNonSpaceChar(customRules, index) {
    const trimmedSubstring = customRules.substring(index + 1).trim();
  
    return trimmedSubstring.charAt(0) || null;
}
  
function previousNonSpaceChar(customRules, index) {
    if (index <= 0 || index >= customRules.length) {
      return null;
    }
  
    const trimmedSubstring = customRules.substring(0, index).trim();
  
    return trimmedSubstring.slice(-1) || null;
} 
